import { createFileRoute } from "@tanstack/react-router";
import { Check, CheckCircle, Copy, CopyIcon } from "lucide-react";
import { useState } from "react";
import { codeToHtml } from "shiki";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import {
  CodeBlock,
  CodeBlockCodeHTML,
  CodeBlockGroup,
} from "~/components/ui/code-block";
import { trpc } from "~/lib/trpc";
import { SettingsTitle } from "~/routes/(app)/settings/-components/settings-title";

const exampleContent = `<html>
  <head>
    // --> Paste the code here, above <--
    // ... any existing code ...
  </head>
  <body>
    ...
  </body>
</html>`;

export const Route = createFileRoute("/(app)/settings/installation")({
  component: RouteComponent,
  loader: async ({ context }) => {
    const scriptContent = await context.queryClient.ensureQueryData(
      trpc.installation.getInstallationScript.queryOptions(),
    );

    const scriptContentHtml = await codeToHtml(scriptContent, {
      lang: "html",
      theme: "github-light",
      structure: "inline",
    });

    const exampleContentHtml = await codeToHtml(exampleContent, {
      lang: "html",
      theme: "github-light",
      structure: "inline",
    });

    return {
      scriptContent,
      scriptContentHtml,
      exampleContentHtml,
      crumb: {
        label: "Installation",
        options: [],
        id: "installation",
      },
    };
  },
});

function RouteComponent() {
  const { scriptContent, scriptContentHtml, exampleContentHtml } =
    Route.useLoaderData();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(scriptContent);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4 w-full">
      <SettingsTitle>Installation</SettingsTitle>

      <div className="space-y-6 w-full text-sm">
        <ol className="space-y-6 w-full">
          <li className="grid grid-cols-[auto_1fr]">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-6 h-6 text-sm -translate-y-px rounded-full bg-green-100 text-green-600">
                1
              </div>
            </div>
            <div className="ml-4 text-sm min-w-0">
              <div>
                <div>
                  <h3 className="text-base font-medium text-gray-900">
                    Copy the code snippet
                  </h3>
                  <p className="mt-1 text-gray-500">
                    Add our script to your website to show your visitors
                    personalized variants, and to track conversions.
                  </p>
                </div>
                <CodeBlock className="mt-3">
                  <CodeBlockGroup className="border-border border-b px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium">
                        HTML
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleCopy}
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </CodeBlockGroup>
                  <CodeBlockCodeHTML inlineHTML={scriptContentHtml} />
                </CodeBlock>
              </div>
            </div>
          </li>

          <li className="flex">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-6 h-6 text-sm -translate-y-px rounded-full bg-green-100 text-green-600">
                2
              </div>
            </div>
            <div className="ml-4 w-full">
              <h3 className="text-base font-medium text-gray-900">
                Paste the code in your {"<head>"} tag
              </h3>
              <p className="mt-1 text-gray-500">
                This ensures that the script is loaded early and works properly.
              </p>
              <CodeBlock className="mt-3">
                <CodeBlockGroup className="border-border border-b px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium">
                      HTML
                    </div>
                  </div>
                </CodeBlockGroup>
                <CodeBlockCodeHTML inlineHTML={exampleContentHtml} />
              </CodeBlock>
            </div>
          </li>
        </ol>

        <div className="bg-green-50 border border-green-300/75 rounded-md py-4.5 px-5 flex items-start">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="ml-3 text-green-700">
            You're all set! No further configuration is needed.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg py-6 px-6">
          <h2 className="text-base font-semibold text-blue-700 mb-1.5">
            Need more help?
          </h2>
          <p className="text-blue-700/80 text-sm">
            If you have any questions or need assistance with installation,
            please contact our support team at{" "}
            <button
              className="font-medium px-1.5 py-0.5 -mx-1.5 -my-0.5 hover:bg-blue-100 rounded-md transition-colors"
              onClick={() => {
                navigator.clipboard.writeText("hello@newmode.ai");
                toast.success("Copied to clipboard", {
                  id: "copy-support-email",
                });
              }}
            >
              hello@newmode.ai{" "}
              <CopyIcon
                className="inline opacity-70 ml-0.5 -translate-y-px"
                size={11}
              />
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
